import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Download, Plus, CheckCircle2 } from "lucide-react";
import { Card } from "../../../../components/ui/Card";
import { cn } from "../../../../libs/utils";

const activityStyles = {
  Active: "text-emerald-600",
  Halt: "text-rose-600",
  Pending: "text-slate-400",
};

/**
 * Batch Details header, matching the wireframe: page-level breadcrumb +
 * Export/Add New Batch actions on top, then a card with the batch title,
 * OK/Total badge, last-updated subtitle, a Complete action, and an
 * activity indicator (Active/Halt).
 */
const BatchDetailsHeader = ({
  listHref = "/setting/batch",
  completeDisabled = false,
  completeHint,
  batchId,
  okParts,
  totalParts,
  lastUpdatedLabel,
  subtitle,
  activity,
  canComplete,
  isCompleted,
  onExport,
  onAddBatch,
  onComplete,
}) => (
  <div className="mb-4">
    <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-1.5 text-sm text-slate-500">
        <Link to={listHref} className="hover:text-brand">
          Batches
        </Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="font-semibold text-slate-800">{batchId}</span>
      </div>

      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={onExport}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-brand/30 bg-white px-3.5 text-sm font-medium text-brand transition-colors hover:bg-lightSecondary"
        >
          <Download className="h-4 w-4" />
          Export
        </button>
        <button
          type="button"
          onClick={onAddBatch}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-brand px-3.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-brandDark"
        >
          <Plus className="h-4 w-4" />
          Add New Batch
        </button>
      </div>
    </div>

    <Card className="p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-900">Batch Details: {batchId}</h1>
            {totalParts != null && (
              <span className="rounded-md bg-lightSecondary px-2 py-0.5 text-xs font-semibold text-brand">
                {okParts ?? "—"} / {totalParts}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {lastUpdatedLabel && <>Last updated : {lastUpdatedLabel} </>}
            {subtitle && <>• {subtitle}</>}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {canComplete && (
            <button
              type="button"
              onClick={onComplete}
              disabled={completeDisabled}
              title={completeHint}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline disabled:cursor-not-allowed disabled:opacity-40"
            >
              <CheckCircle2 className="h-4 w-4" />
              Complete
            </button>
          )}
          {isCompleted && (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />
              Completed
            </span>
          )}
          {activity && (
            <span className={cn("flex items-center gap-1.5 text-xs font-semibold", activityStyles[activity] || "text-slate-400")}>
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              {activity}
            </span>
          )}
        </div>
      </div>
    </Card>
  </div>
);

export default BatchDetailsHeader;
